import { apiService } from './api';

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  phone?: string;
  avatar?: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: string;
  permissions?: string[];
  phone?: string;
  isActive?: boolean;
}

export interface UpdateUserDto {
  name?: string;
  email?: string;
  password?: string;
  role?: string;
  permissions?: string[];
  phone?: string;
  isActive?: boolean;
}

class UserManagementService {
  async getUsers(): Promise<AdminUser[]> {
    const response = await apiService.get<AdminUser[]>('/api/admin-users');
    return response.success ? response.data || [] : [];
  }

  async getUser(id: string): Promise<AdminUser | null> {
    const response = await apiService.get<AdminUser>(`/api/admin-users/${id}`);
    return response.success ? response.data || null : null;
  }

  async createUser(data: CreateUserDto): Promise<AdminUser | null> {
    const response = await apiService.post<AdminUser>('/api/admin-users', data);
    return response.success ? response.data || null : null;
  }

  async updateUser(id: string, data: UpdateUserDto): Promise<AdminUser | null> {
    const response = await apiService.put<AdminUser>(`/api/admin-users/${id}`, data);
    return response.success ? response.data || null : null;
  }

  async updatePermissions(id: string, permissions: string[]): Promise<AdminUser | null> {
    const response = await apiService.put<AdminUser>(`/api/admin-users/${id}/permissions`, { permissions });
    return response.success ? response.data || null : null;
  }

  async toggleActive(id: string): Promise<AdminUser | null> {
    const response = await apiService.put<AdminUser>(`/api/admin-users/${id}/toggle-active`, {});
    return response.success ? response.data || null : null;
  }

  async deleteUser(id: string): Promise<boolean> {
    const response = await apiService.delete(`/api/admin-users/${id}`);
    return response.success;
  }

  async getRoles(): Promise<Role[]> {
    const response = await apiService.get<Role[]>('/api/admin-users/roles');
    return response.success ? response.data || [] : [];
  }

  async getPermissions(): Promise<Permission[]> {
    const response = await apiService.get<Permission[]>('/api/admin-users/permissions');
    return response.success ? response.data || [] : [];
  }

  async login(email: string, password: string): Promise<{ user: AdminUser; token: string } | null> {
    const response = await apiService.post<{ user: AdminUser; token: string }>('/api/admin-users/login', { email, password });
    return response.success ? response.data || null : null;
  }

  // Role display helpers
  getRoleColor(role: string): string {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800',
      seo_manager: 'bg-blue-100 text-blue-800',
      fleet_manager: 'bg-green-100 text-green-800',
      booking_manager: 'bg-orange-100 text-orange-800',
      analytics_viewer: 'bg-cyan-100 text-cyan-800',
      content_editor: 'bg-pink-100 text-pink-800',
      custom: 'bg-gray-100 text-gray-800',
    };
    return colors[role] || colors.custom;
  }

  // Role icons are now handled in the component with SVG icons
  getRoleIconName(role: string): string {
    const icons: Record<string, string> = {
      admin: 'crown',
      seo_manager: 'chart',
      fleet_manager: 'car',
      booking_manager: 'calendar',
      analytics_viewer: 'analytics',
      content_editor: 'pencil',
      custom: 'cog',
    };
    return icons[role] || icons.custom;
  }
}

export const userManagementService = new UserManagementService();
